import { appConfirm } from '../../services/appDialogs';
import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import { X, Search, UserPlus, Trash2, Users } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";

/** 尝试 decode URL 编码的中文名，失败则原样返回 */
const decodeName = (name) => {
  try { return decodeURIComponent(name); } catch { return name; }
};

const ROLE_LABEL = { owner: "创建者", editor: "协作者" };

const ProjectMemberModal = ({ projectId, userId, canManage = false, onClose }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [members, setMembers] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => { fetchMembers(); }, [projectId]);

  // owner 排最前
  const sortedMembers = useMemo(() =>
    [...members].sort((a, b) => (a.role === "owner" ? -1 : b.role === "owner" ? 1 : 0)),
    [members]
  );

  const fetchMembers = async () => {
    try {
      const data = await authenticatedFetch(
        `${API_HOST}/api/project/member/list?projectId=${projectId}`,
        { headers: { "X-User-ID": userId } }
      );
      setMembers(data || []);
    } catch (e) {
      console.error(e);
      toast.error("加载成员列表失败");
    }
  };

  const handleSearch = async (keyword) => {
    setSearchKeyword(keyword);
    if (!keyword.trim()) { setSearchResults([]); return; }
    try {
      const data = await authenticatedFetch(
        `${API_HOST}/api/project/member/search?keyword=${encodeURIComponent(keyword)}`
      );
      const memberIds = new Set(members.map((m) => m.userId));
      setSearchResults((data || []).filter((name) => !memberIds.has(name)));
    } catch (e) {
      console.error(e);
      toast.error("搜索用户失败");
    }
  };

  const handleAdd = async (userName) => {
    try {
      await authenticatedFetch(`${API_HOST}/api/project/member/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-ID": userId },
        body: JSON.stringify({ projectId, userName }),
      });
      toast.success(`已添加 ${decodeName(userName)}`);
      setSearchKeyword("");
      setSearchResults([]);
      fetchMembers();
    } catch (e) {
      toast.error("添加失败: " + e.message);
    }
  };

  const handleRemove = async (userName) => {
    if (!(await appConfirm(`确定移除 ${decodeName(userName)} 吗？`))) return;
    try {
      await authenticatedFetch(
        `${API_HOST}/api/project/member/remove?projectId=${projectId}&userName=${encodeURIComponent(userName)}`,
        { method: "DELETE", headers: { "X-User-ID": userId } }
      );
      toast.success(`已移除 ${decodeName(userName)}`);
      fetchMembers();
    } catch (e) {
      toast.error("移除失败: " + e.message);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-[100]">
      <DialogBackdrop
        transition
        className="ui-dialog-backdrop fixed inset-0 backdrop-blur-md"
        style={{ backgroundColor: 'var(--overlay-light)' }}
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
      <DialogPanel
        transition
        className="ui-dialog-panel relative rounded-2xl w-full max-w-[500px] max-h-[80vh] shadow-2xl overflow-hidden flex flex-col"
        style={{
          backgroundColor: "var(--color-bg-secondary)",
          border: "1px solid var(--color-border-primary)",
        }}
      >
        <div className="p-6 pb-0">
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h2
              className="text-lg font-bold flex items-center gap-2"
              style={{ color: "var(--color-text-primary)" }}
            >
              <Users size={18} style={{ color: theme.colors.accentPrimary }} />
              {canManage ? "成员管理" : "项目成员"}
            </h2>
            <button
              onClick={onClose}
              className="ui-pressable p-2 rounded-full"
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={18} />
            </button>
          </div>

          {/* 搜索添加 - 仅管理者可见 */}
          {canManage && (
            <div className="relative mb-4">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: "var(--color-text-muted)" }}
              />
              <input
                value={searchKeyword}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none"
                style={{
                  backgroundColor: "var(--color-bg-overlay)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                }}
                placeholder="搜索用户名添加成员..."
              />
              {searchResults.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-10 max-h-40 overflow-y-auto"
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border-primary)",
                  }}
                >
                  {searchResults.map((name) => (
                    <button
                      key={name}
                      onClick={() => handleAdd(name)}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm transition-all duration-200 hover:opacity-80"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      <span>{decodeName(name)}</span>
                      <UserPlus
                        size={14}
                        style={{ color: theme.colors.accentPrimary }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 成员列表 */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <div className="space-y-2">
            {sortedMembers.map((member) => {
              const displayName = decodeName(member.userId);
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{
                    backgroundColor: "var(--color-bg-overlay)",
                    border: "1px solid var(--color-border-primary)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        backgroundColor:
                          member.role === "owner"
                            ? "var(--color-accent-primary)"
                            : "var(--color-bg-tertiary)",
                        color:
                          member.role === "owner"
                            ? "white"
                            : "var(--color-text-secondary)",
                        border: "1px solid var(--color-border-primary)",
                      }}
                    >
                      {displayName.substring(0, 1)}
                    </div>
                    <div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: "var(--color-text-primary)" }}
                      >
                        {displayName}
                      </span>
                      <span
                        className="ml-2 text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor:
                            member.role === "owner"
                              ? `${theme.colors.accentPrimary}20`
                              : "var(--color-bg-tertiary)",
                          color:
                            member.role === "owner"
                              ? theme.colors.accentPrimary
                              : "var(--color-text-muted)",
                        }}
                      >
                        {ROLE_LABEL[member.role] || member.role}
                      </span>
                    </div>
                  </div>

                  {canManage && member.role !== "owner" && (
                    <button
                      onClick={() => handleRemove(member.userId)}
                      className="p-1.5 rounded-lg transition-all duration-200"
                      style={{ color: "var(--color-text-muted)" }}
                      title="移除成员"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DialogPanel>
      </div>
    </Dialog>
  );
};

export default ProjectMemberModal;
